import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { getShadowtrack } from '@racemap/utilities/functions/map';
import { isRacemapTrack } from '@racemap/utilities/functions/validation';
import { RacemapGeoTypes } from '@racemap/utilities/types/geos';
import type { Immutable } from 'immer';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CurrentEvent } from 'src/store/events/events_reducers';
import type { CheckpointEntry } from '../../BasicComponents/CheckList';
import { MapEditorLink } from '../../BasicComponents/Links';
import { HStack } from '../../BasicComponents/MetaComponent';
import { PopoverHint } from '../../BasicComponents/PopoverHint';
import MapPreview from '../../MapPreview';
import { Checkpoint } from './Checkpoint';

interface Props {
  event: Immutable<CurrentEvent> | null;
}

export const ShadowtrackCheckpoint: FC<Props> = ({ event }) => {
  const navigate = useNavigate();
  if (event == null) return null;

  const shadowtrack = getShadowtrack(event);
  const mapTabUrl = `/admin/events/${event.id}/map`;
  const numberOfTracks = event.geo.features?.filter(isRacemapTrack).length ?? 0;

  if (shadowtrack != null) {
    return (
      <Checkpoint hint={<Hint />}>
        <HStack align="start">
          <div style={{ marginRight: '5px' }}>
            <div>
              <strong>Shadowtrack:</strong>
              <PopoverHint>
                <Hint />
              </PopoverHint>
            </div>
            The track {shadowtrack.properties.name} is set as the shadowtrack. You can change or
            adjust the shadowtrack in the{' '}
            <MapEditorLink
              eventId={event.id}
              geoElementType={RacemapGeoTypes.TRACK}
              geoElementId={shadowtrack.id}
            />
            .
          </div>
          <IconButton onClick={() => navigate(mapTabUrl)} style={{ width: 120 }}>
            <MapPreview width={100} height={100} event={event} />
          </IconButton>
        </HStack>
      </Checkpoint>
    );
  }
  if (numberOfTracks != null && numberOfTracks > 0) {
    return (
      <Checkpoint title="Shadowtrack" hint={<Hint />}>
        You have created <strong>{numberOfTracks}</strong> track(s) in the map settings. But you
        have not set the shadowtrack of this event. Set the correct track as the shadowtrack in the{' '}
        <MapEditorLink eventId={event.id} geoElementType={RacemapGeoTypes.TRACK} />.
      </Checkpoint>
    );
  }
  return (
    <Checkpoint title="Shadowtrack" hint={<Hint />}>
      You have not provided one or multiple tracks, yet. Upload or create tracks for this event in
      the <MapEditorLink eventId={event.id} geoElementType={RacemapGeoTypes.TRACK} />.
    </Checkpoint>
  );
};

export function getShadowtrackCheckpointState(
  event: Immutable<CurrentEvent> | null,
): CheckpointEntry['status'] {
  const shadowtrack = getShadowtrack(event);

  if (shadowtrack != null) {
    return 'success';
  }
  return 'danger';
}

const Hint: FC = () => (
  <>
    Only one track can be set as shadowtrack of a tracking map. Predictive tracking calculates the
    smooth movement of participants along the shadowtrack from the start- to the finish location.
    The shadowtrack represents the route on which participants move from start to finish.
    <ul>
      <li>
        The length of the shadowtrack has to be the same length as the contest e.g. 42,195 km for a
        marathon.
      </li>
      <li>
        The shadowtrack for a triathlon includes swimming, cycling, and running in one continuous
        line.
      </li>
      <li>If athletes have to run three laps, then the shadowtrack must include three laps.</li>
    </ul>
  </>
);

const IconButton = styled.div`
  padding: 0px;
  background-color: #fff;
  border: 3px solid ${RacemapColors.LightGray};
  border-radius: 10px;
  :hover {
    background-color: #fff;
    border: 3px solid ${RacemapColors.LightGray};
  }
`;
